'use client';

import React from 'react';
import Link from 'next/link';
import { Coffee, BookOpen, Package, DollarSign, ArrowRight, Wrench } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const upcomingFeatures = [
  {
    icon: BookOpen,
    label: 'إدارة الوصفات',
    labelFr: 'Gestion des Recettes',
    desc: 'أنشئ وصفاتك وحدد مكوناتها وكمياتها بدقة',
    href: '/cafe-restaurant/recipes',
    color: 'text-amber-600',
    bg: 'bg-amber-50',
  },
  {
    icon: Package,
    label: 'مخزون المواد الأولية',
    labelFr: 'Stock Matières Premières',
    desc: 'تتبع الحليب، القهوة، الدقيق وكل المواد الأولية',
    href: '/cafe-restaurant/ingredients',
    color: 'text-orange-600',
    bg: 'bg-orange-50',
  },
  {
    icon: DollarSign,
    label: 'تكاليف الإنتاج',
    labelFr: 'Coûts de Production',
    desc: 'احسب تكلفة كل طبق وهامش الربح المثالي',
    href: '/cafe-restaurant/production-cost',
    color: 'text-rose-600',
    bg: 'bg-rose-50',
  },
];

export default function CafeDashboardStub() {
  return (
    <div className="space-y-8">
      {/* Hero */}
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-8 text-center">
        <div className="w-16 h-16 rounded-2xl bg-amber-600 flex items-center justify-center mx-auto mb-4">
          <Coffee size={32} className="text-white" />
        </div>
        <h1 className="text-2xl font-bold text-foreground mb-2">مقهى / مطعم</h1>
        <p className="text-sm text-muted-foreground mb-4 max-w-lg mx-auto">
          واجهة مخصصة لأصحاب المقاهي والمطاعم — إدارة الوصفات، المواد الأولية، وتكاليف الإنتاج
        </p>
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 border border-amber-300 rounded-full">
          <Wrench size={14} className="text-amber-700" />
          <span className="text-sm font-semibold text-amber-800">المرحلة الثانية — قيد التطوير</span>
        </div>
      </div>
      {/* Upcoming features */}
      <div>
        <h2 className="text-sm font-semibold text-foreground mb-4">الخصائص القادمة — Fonctionnalités à venir</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {upcomingFeatures?.map(feature => {
            const Icon = feature?.icon;
            return (
              <Link
                key={feature?.href}
                href={feature?.href}
                className="bg-card border border-border rounded-xl p-5 hover:shadow-sm hover:border-amber-300 transition-all group"
              >
                <div className={`w-10 h-10 rounded-xl ${feature?.bg} flex items-center justify-center mb-3`}>
                  <Icon size={20} className={feature?.color} />
                </div>
                <h3 className="text-sm font-semibold text-foreground mb-1">{feature?.label}</h3>
                <p className="text-xs text-muted-foreground mb-3">{feature?.desc}</p>
                <div className="flex items-center gap-1 text-xs text-amber-600 font-medium group-hover:gap-2 transition-all">
                  <span>استعراض</span>
                  <ArrowRight size={12} />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
      {/* Difference from epicerie */}
      <div className="bg-card border border-border rounded-xl p-5">
        <h3 className="text-sm font-semibold text-foreground mb-4">ما الفرق عن واجهة الحانوت؟</h3>
        <div className="grid grid-cols-2 gap-4 text-xs">
          <div>
            <p className="font-semibold text-foreground mb-2">🏪 حانوت / بقالة</p>
            <ul className="space-y-1 text-muted-foreground">
              <li>• POS بالباركود</li>
              <li>• مخزون منتجات جاهزة</li>
              <li>• كريدي الزبائن</li>
              <li>• طلبيات من الموردين</li>
            </ul>
          </div>
          <div>
            <p className="font-semibold text-foreground mb-2">☕ مقهى / مطعم</p>
            <ul className="space-y-1 text-muted-foreground">
              <li>• إدارة الوصفات والمكونات</li>
              <li>• مخزون مواد أولية</li>
              <li>• حساب تكلفة الإنتاج</li>
              <li>• هامش ربح لكل طبق</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
